import { useCallback, useRef } from 'react';
import {
  calculateBoundingBox,
  calculateCenteredPan,
  calculateFitZoom,
  calculateCenterTaskZoom,
  calculateStartPositionPan,
  contentFitsInViewport,
  canSkipAnimation,
  DEFAULT_NODE_DIMS,
} from '../utils/viewResetUtils';
import {
  runPanZoomAnimation,
  runPanAnimation,
  cancelAnimation,
  isPanNegligible,
} from '../utils/animationRunner';

/**
 * useFocusViewReset Hook
 * 
 * Handles view reset and animation for focus mode:
 * - Centering on tasks
 * - Animated pan/zoom transitions
 * - Pan back when exiting focus mode
 * 
 * Performance: Uses direct DOM manipulation during animations to avoid
 * React re-renders on every frame, then syncs state at the end.
 */
export function useFocusViewReset({
  containerRef,
  nodePositions,
  nodeDimensions,
  allFocusedTaskIds,
  focusAutoPositions,
  pan,
  zoom,
  setPan,
  setZoom,
}) {
  // Keep refs for pan/zoom to avoid dependency issues in animation callbacks
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);
  // Animation refs for cleanup (separate refs to prevent conflicts)
  const centerAnimationRef = useRef(null);
  const zoomAnimationRef = useRef(null);
  if (!centerAnimationRef.current) {
    panRef.current = pan;
  }
  zoomRef.current = zoom;
  
  // Keep refs for focus data to avoid stale closures in setTimeout callbacks
  const allFocusedTaskIdsRef = useRef(allFocusedTaskIds);
  const focusAutoPositionsRef = useRef(focusAutoPositions);
  allFocusedTaskIdsRef.current = allFocusedTaskIds;
  focusAutoPositionsRef.current = focusAutoPositions;
  
  // Keep refs for node data to avoid callback recreation
  const nodeDimensionsRef = useRef(nodeDimensions);
  const nodePositionsRef = useRef(nodePositions);
  nodeDimensionsRef.current = nodeDimensions;
  nodePositionsRef.current = nodePositions;
  
  // Direct DOM update for smoother animations (bypasses React re-renders)
  const updateCanvasTransform = useCallback((newPan, newZoom) => {
    const canvas = containerRef.current?.querySelector('.flowchart__canvas');
    if (canvas) {
      canvas.style.transform = `translate(${newPan.x}px, ${newPan.y}px) scale(${newZoom})`;
      canvas.style.setProperty('--inverse-zoom', 1 / newZoom);
      // Dispatch event for minimap to sync viewport
      canvas.dispatchEvent(new CustomEvent('panupdate', { detail: { x: newPan.x, y: newPan.y, zoom: newZoom } }));
    }
  }, [containerRef]);
  
  // Center view on a specific task with smooth animation and optional zoom adjustment
  const centerOnTask = useCallback((taskId, options = {}) => {
    if (!containerRef.current || !nodePositionsRef.current[taskId]) return;
    
    cancelAnimation(centerAnimationRef);
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const pos = nodePositionsRef.current[taskId];
    const dims = nodeDimensionsRef.current[taskId] || DEFAULT_NODE_DIMS;
    
    const taskCenterX = pos.x + dims.width / 2;
    const taskCenterY = pos.y + dims.height / 2;
    
    const { adjustZoom = true, duration = 300 } = options;
    const currentZoom = zoomRef.current;
    const targetZoom = calculateCenterTaskZoom(currentZoom, adjustZoom);
    
    const targetPan = calculateCenteredPan({
      contentCenterX: taskCenterX,
      contentCenterY: taskCenterY,
      viewportWidth: containerRect.width,
      viewportHeight: containerRect.height,
      zoom: targetZoom,
    });
    
    const deltaX = targetPan.x - panRef.current.x;
    const deltaY = targetPan.y - panRef.current.y;
    const deltaZoom = targetZoom - currentZoom;
    
    // Skip animation if already at target
    if (canSkipAnimation({ deltaX, deltaY, deltaZoom })) {
      setPan(targetPan);
      if (Math.abs(deltaZoom) >= 0.01) {
        setZoom(targetZoom, true);
      }
      return;
    }
    
    runPanZoomAnimation({
      startPan: { ...panRef.current },
      targetPan,
      startZoom: currentZoom,
      targetZoom,
      duration,
      refs: { panRef, zoomRef, animationRef: centerAnimationRef },
      updateCanvasTransform,
      setPan,
      setZoom,
    });
  }, [containerRef, setPan, setZoom, updateCanvasTransform]);
  
  // Pan to a specific position (x, y) with smooth animation
  // Perfect for panning to newly created tasks where position is known but not yet in nodePositionsRef
  const panToPosition = useCallback((position, options = {}) => {
    if (!containerRef.current || !position) return;
    
    cancelAnimation(centerAnimationRef);

    const canvas = containerRef.current.querySelector('.flowchart__canvas');
    const liveTransform = canvas?.style?.transform || '';
    const livePanMatch = liveTransform.match(/translate\(\s*(-?[\d.]+)px\s*,\s*(-?[\d.]+)px\s*\)/);
    if (livePanMatch) {
      panRef.current = { x: parseFloat(livePanMatch[1]), y: parseFloat(livePanMatch[2]) };
    }
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const { width = DEFAULT_NODE_DIMS.width, height = DEFAULT_NODE_DIMS.height } = options;
    
    const targetCenterX = position.x + width / 2;
    const targetCenterY = position.y + height / 2;
    
    const { adjustZoom = false, duration = 300 } = options;
    const currentZoom = zoomRef.current;
    const targetZoom = calculateCenterTaskZoom(currentZoom, adjustZoom);
    
    const targetPan = calculateCenteredPan({
      contentCenterX: targetCenterX,
      contentCenterY: targetCenterY,
      viewportWidth: containerRect.width,
      viewportHeight: containerRect.height,
      zoom: targetZoom,
    });
    
    const deltaX = targetPan.x - panRef.current.x;
    const deltaY = targetPan.y - panRef.current.y;
    const deltaZoom = targetZoom - currentZoom;
    
    if (canSkipAnimation({ deltaX, deltaY, deltaZoom })) {
      setPan(targetPan);
      if (Math.abs(deltaZoom) >= 0.01) {
        setZoom(targetZoom, true);
      }
      return;
    }
    
    runPanZoomAnimation({
      startPan: { ...panRef.current },
      targetPan,
      startZoom: currentZoom,
      targetZoom,
      duration,
      refs: { panRef, zoomRef, animationRef: centerAnimationRef },
      updateCanvasTransform,
      setPan,
      setZoom,
    });
  }, [containerRef, setPan, setZoom, updateCanvasTransform]);
  
  // Zoom out FIRST when exiting focus mode - to show where nodes will return to
  const zoomOutForExit = useCallback((focusedIds) => {
    cancelAnimation(zoomAnimationRef);
    
    if (!containerRef.current || !focusedIds || focusedIds.size === 0) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const PADDING = 80;
    
    const boundingBox = calculateBoundingBox(focusedIds, nodePositionsRef.current, nodeDimensionsRef.current);
    if (!boundingBox) return;
    
    const viewportWidth = containerRect.width - PADDING * 2;
    const viewportHeight = containerRect.height - PADDING * 2;
    const currentZoom = zoomRef.current;
    
    // Only zoom out if content doesn't fit at current zoom
    const needsZoomOut = !contentFitsInViewport({
      contentWidth: boundingBox.width,
      contentHeight: boundingBox.height,
      viewportWidth,
      viewportHeight,
      zoom: currentZoom,
    });
    
    if (!needsZoomOut) return;
    
    const targetZoom = calculateFitZoom({
      contentWidth: boundingBox.width,
      contentHeight: boundingBox.height,
      viewportWidth,
      viewportHeight,
    });
    
    const targetPan = calculateCenteredPan({
      contentCenterX: boundingBox.centerX,
      contentCenterY: boundingBox.centerY,
      viewportWidth: containerRect.width,
      viewportHeight: containerRect.height,
      zoom: targetZoom,
    });
    
    runPanZoomAnimation({
      startPan: { ...panRef.current },
      targetPan,
      startZoom: zoomRef.current,
      targetZoom,
      duration: 300,
      refs: { panRef, zoomRef, animationRef: zoomAnimationRef },
      updateCanvasTransform,
      setPan,
      setZoom,
    });
  }, [containerRef, setPan, setZoom, updateCanvasTransform]);
  
  // Pan to follow nodes as they return to original positions
  const panBackToNodes = useCallback((focusedIds) => {
    cancelAnimation(centerAnimationRef);
    
    if (!containerRef.current || !focusedIds || focusedIds.size === 0) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const boundingBox = calculateBoundingBox(focusedIds, nodePositionsRef.current, nodeDimensionsRef.current);
    if (!boundingBox) return;
    
    const currentZoom = zoomRef.current;
    const targetPan = calculateCenteredPan({
      contentCenterX: boundingBox.centerX,
      contentCenterY: boundingBox.centerY,
      viewportWidth: containerRect.width,
      viewportHeight: containerRect.height,
      zoom: currentZoom,
    });
    
    // Skip animation if we're already within 5px of target
    if (isPanNegligible(panRef.current, targetPan)) return;
    
    runPanAnimation({
      startPan: { ...panRef.current },
      targetPan,
      currentZoom,
      duration: 400,
      refs: { panRef, animationRef: centerAnimationRef },
      updateCanvasTransform,
      setPan,
    });
  }, [containerRef, setPan, updateCanvasTransform]);
  
  // Pan to focus layout (no zoom) - used during entering-move phase
  const panToFocusLayout = useCallback(() => {
    const focusedIds = allFocusedTaskIdsRef.current;
    const focusPositions = focusAutoPositionsRef.current;
    
    if (!containerRef.current || !focusedIds || !focusPositions) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const boundingBox = calculateBoundingBox(focusedIds, focusPositions, nodeDimensionsRef.current);
    if (!boundingBox) return;
    
    const currentZoom = zoomRef.current;
    const targetPan = calculateCenteredPan({
      contentCenterX: boundingBox.centerX,
      contentCenterY: boundingBox.centerY,
      viewportWidth: containerRect.width,
      viewportHeight: containerRect.height,
      zoom: currentZoom,
    });
    
    cancelAnimation(zoomAnimationRef);
    
    runPanAnimation({
      startPan: { ...panRef.current },
      targetPan,
      currentZoom,
      duration: 350,
      refs: { panRef, animationRef: zoomAnimationRef },
      updateCanvasTransform,
      setPan,
    });
  }, [containerRef, setPan, updateCanvasTransform]);
  
  // Zoom to 1.0 (normal zoom) after entering focus mode
  const zoomToFocusLayout = useCallback(() => {
    const focusedIds = allFocusedTaskIdsRef.current;
    const focusPositions = focusAutoPositionsRef.current;
    
    if (!containerRef.current || !focusedIds || !focusPositions) return;
    
    const currentZoom = zoomRef.current;
    const targetZoom = 1.0;
    
    // Skip if already at 1.0 (within tolerance)
    if (Math.abs(currentZoom - targetZoom) < 0.05) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const boundingBox = calculateBoundingBox(focusedIds, focusPositions, nodeDimensionsRef.current);
    if (!boundingBox) return;
    
    const targetPan = calculateStartPositionPan({
      boundingBox,
      viewportWidth: containerRect.width,
      viewportHeight: containerRect.height,
      zoom: targetZoom,
    });
    
    cancelAnimation(zoomAnimationRef);
    
    runPanZoomAnimation({
      startPan: { ...panRef.current },
      targetPan,
      startZoom: currentZoom,
      targetZoom,
      duration: 300,
      refs: { panRef, zoomRef, animationRef: zoomAnimationRef },
      updateCanvasTransform,
      setPan,
      setZoom,
    });
  }, [containerRef, setPan, setZoom, updateCanvasTransform]);
  
  // Reset view for focus mode with animated pan/zoom (legacy - combines both)
  const resetViewForFocusMode = useCallback(() => {
    panToFocusLayout();
  }, [panToFocusLayout]);
  
  // Reset view for focus mode INSTANTLY (no animation - for view switching)
  const resetViewForFokusModeInstant = useCallback(() => {
    if (!containerRef.current || !allFocusedTaskIds || !focusAutoPositions) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    
    const boundingBox = calculateBoundingBox(allFocusedTaskIds, focusAutoPositions, nodeDimensions);
    if (!boundingBox) return;
    
    const targetPan = calculateStartPositionPan({
      boundingBox,
      viewportWidth: containerRect.width,
      viewportHeight: containerRect.height,
      zoom: 1.0,
    });
    
    cancelAnimation(centerAnimationRef);
    
    setZoom(1, true);
    setPan(targetPan);
  }, [containerRef, nodeDimensions, allFocusedTaskIds, focusAutoPositions, setPan, setZoom]);
  
  return {
    centerOnTask,
    panToPosition,
    resetViewForFocusMode,
    resetViewForFokusModeInstant,
    panBackToNodes,
    panToFocusLayout,
    zoomToFocusLayout,
    zoomOutForExit,
  };
}
